import { ORDER_STATUS_LABELS } from "../orderStatus";
import type { OrderStatus } from "../../api/types";

/**
 * Finding an order, and narrowing the ones on show.
 *
 * Every control here submits to the backend. None of them filters the page in
 * the browser: the list is paginated server-side, so a local filter would
 * search the twenty-five rows in front of the administrator and quietly report
 * that an order on page two does not exist.
 *
 * ONE SEARCH BOX, THREE FIELDS
 * The backend matches the order number, the customer's name and their phone
 * number in a single pass. Splitting this into three inputs would ask an
 * operator with a customer on the phone to decide which field the thing in
 * their hand is — and they are usually holding whichever one the customer
 * happened to say.
 */

const STATUS_ORDER: OrderStatus[] = [
  "placed",
  "confirmed",
  "fulfilment_pending",
  "fulfilled",
  "cancelled",
];

export function OrdersToolbar({
  search,
  status,
  createdFrom,
  createdTo,
  total,
  onSearch,
  onStatus,
  onCreatedFrom,
  onCreatedTo,
  onClear,
}: {
  search: string;
  status: string;
  createdFrom: string;
  createdTo: string;
  /** null until the backend has answered, so no count is shown while loading. */
  total: number | null;
  onSearch: (next: string) => void;
  onStatus: (next: string) => void;
  onCreatedFrom: (next: string) => void;
  onCreatedTo: (next: string) => void;
  onClear: () => void;
}) {
  const narrowed = Boolean(search || status || createdFrom || createdTo);

  return (
    <div className="orders__toolbar">
      <div className="orders__search">
        <label className="sr-only" htmlFor="order-search">
          Search orders by number, customer name or phone
        </label>
        <input
          id="order-search"
          type="search"
          placeholder="Order number, customer name or phone"
          // Uncontrolled with a key, so typing is never interrupted by a
          // refetch and clearing the filters genuinely empties the box.
          key={`search-${search}`}
          defaultValue={search}
          onKeyDown={(event) => {
            if (event.key === "Enter") onSearch(event.currentTarget.value);
          }}
          onBlur={(event) => {
            if (event.currentTarget.value !== search) {
              onSearch(event.currentTarget.value);
            }
          }}
        />
      </div>

      <div className="orders__filter">
        <label className="sr-only" htmlFor="order-status-filter">
          Lifecycle status
        </label>
        <select
          id="order-status-filter"
          value={status}
          onChange={(event) => onStatus(event.target.value)}
        >
          <option value="">All statuses</option>
          {STATUS_ORDER.map((value) => (
            <option key={value} value={value}>
              {ORDER_STATUS_LABELS[value]}
            </option>
          ))}
        </select>
      </div>

      {/* Both ends name a calendar day as the OPERATOR sees it, and both are
          inclusive of the whole of that day. The screen converts each into the
          moment it really began and ended where the operator is standing
          (`orders/dateRange`), because the table renders every timestamp in
          that same local timezone — so the day picked here is the day shown
          there. Filtering in UTC while displaying locally is how an order
          visibly dated 14 August disappears from a search for 14 August. */}
      <div className="orders__dates">
        <label htmlFor="order-created-from">From</label>
        <input
          id="order-created-from"
          type="date"
          value={createdFrom}
          max={createdTo || undefined}
          onChange={(event) => onCreatedFrom(event.target.value)}
        />
        <label htmlFor="order-created-to">To</label>
        <input
          id="order-created-to"
          type="date"
          value={createdTo}
          min={createdFrom || undefined}
          onChange={(event) => onCreatedTo(event.target.value)}
        />
      </div>

      <div className="spacer" />

      {narrowed ? (
        <button type="button" className="btn btn--sm" onClick={onClear}>
          Clear filters
        </button>
      ) : null}

      {total !== null ? (
        <span className="muted num" data-testid="orders-total">
          {total} order{total === 1 ? "" : "s"}
        </span>
      ) : null}
    </div>
  );
}
